import * as React from 'react';
import { useEffect, useState } from 'react';
import { Collapse, notification, Table } from '@duxton-web/ui';

import {
  additionalPolicies,
  blacklistedPolicyNames,
  columnOrderByPolicyName,
  PolicyName,
} from '../constants';
import { getShowPublisherFlag } from '../utils';
import { getColumns, getDataSource } from './AccessControlTableUtils';
import { EVENT } from '@/constants';
import { useUserRolesContext } from '@/contexts/UserRolesContext';
// eslint-disable-next-line import/no-named-as-default
import Service, { Policy } from '@/lib/App';
import { formatHasAccessError } from '@/utils/url';

export const AccessControlTable = () => {
  const [state, setState] = useState<{
    policies: Policy[];
    isError: boolean;
    errorMessage: string;
    isCollapsed: boolean;
    showPublisherFlag: boolean;
  }>({
    policies: [],
    isError: false,
    errorMessage: '',
    isCollapsed: false,
    showPublisherFlag: false,
  });

  const { userTFRoles, isAdmin } = useUserRolesContext();

  const toggleCollapsed = () => {
    setState((cur) => ({ ...cur, isCollapsed: !cur.isCollapsed }));
  };

  useEffect(() => {
    const showPublisherFlag = getShowPublisherFlag();

    Service.planner
      .fetchAllPolicies()
      .then((policies) => {
        let currBlacklistedPolicyNames = [...blacklistedPolicyNames];

        if (showPublisherFlag) {
          currBlacklistedPolicyNames = currBlacklistedPolicyNames.filter(
            (x) => x !== PolicyName.PUBLISHER,
          );
          currBlacklistedPolicyNames = [
            ...currBlacklistedPolicyNames,
            PolicyName.EXPERIMENT_MODERATOR,
            PolicyName.ROLLOUT_MODERATOR,
          ];
        }

        policies = [
          ...policies.filter(
            (p) => !currBlacklistedPolicyNames.includes(p.policyName),
          ),
          ...additionalPolicies,
        ];

        policies.sort(
          (p1, p2) =>
            getColumnOrder(p1.policyName) - getColumnOrder(p2.policyName),
        );

        setState((cur) => ({
          ...cur,
          policies,
          showPublisherFlag,
        }));
      })
      .catch((error) => {
        console.error(error);
        const description = `Retrieving Policies failed`;
        setState((cur) => ({
          ...cur,
          isError: true,
          errorMessage: description,
        }));

        notification.error({
          message: `Retrieving Policies failed`,
          description: error?.response?.data?.arg ? (
            <div>Error: {error?.response?.data?.arg}</div>
          ) : null,
        });
      });
  }, []);

  const { policies, isError, errorMessage, isCollapsed } = state;

  const techFamilies = Service.cache.getAllTechFamilies();
  const columns = getColumns(policies);
  const dataSource = getDataSource(
    userTFRoles,
    policies,
    techFamilies,
    isAdmin,
  );

  const locale =
    dataSource.length === 0
      ? {
          emptyText: (
            <>{formatHasAccessError(EVENT, state.showPublisherFlag)}</>
          ),
        }
      : {};

  return (
    <section style={{ padding: '10px 0px' }}>
      <Collapse
        activeKey={isCollapsed ? [] : ['user-acl-table']}
        onChange={() => toggleCollapsed()}
      >
        <Collapse.Panel header="ACL table" key="user-acl-table">
          {isError ? (
            <h3> {errorMessage} </h3>
          ) : (
            <Table
              size="middle"
              className="grab-x__table"
              columns={columns}
              dataSource={dataSource}
              pagination={false}
              locale={locale}
            />
          )}
        </Collapse.Panel>
      </Collapse>
    </section>
  );
};

// getColumnOrder gets column order by policy name
function getColumnOrder(policyName: string): number {
  const defaultOrder = 1000;
  return columnOrderByPolicyName[policyName] || defaultOrder;
}

export default AccessControlTable;
